import { ActionIcon, Box, Loader, Paper, Text, Title } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconX } from "@tabler/icons-react";
import { queryOptions, useSuspenseQueries } from "@tanstack/react-query";
import { Suspense } from "react";
import { httpPostGraphql } from "shared/api/httpClient";
import { MapBase } from "shared/components/map/MapBase";
import { MapFeatureOverlay } from "shared/components/map/MapFeatureOverlay";
import { useComposeFormContext } from "shared/components/message/compose/context";
import { useFormState } from "shared/hooks/useFormState";
import { qk } from "shared/utils/qk";
import { geozod } from "shared/utils/zod";
import { graphql } from "src/gql";

type AudienceGeoCardProps = {
    widget?: boolean;
    onDelete?: () => unknown;
};

export const AudienceGeoCard = (props: AudienceGeoCardProps) => {
    const form = useComposeFormContext();
    const [features] = useFormState(form, "audience.features");
    const [geofilterIds] = useFormState(form, "audience.geofilters");

    const geofilters = useSuspenseQueries({
        queries: geofilterIds.map(query),
    });

    return (
        <Paper withBorder radius="md" w="min-content" shadow="md" tabIndex={-1} pos={"relative"}>
            {!props.widget && (
                <ActionIcon
                    variant="default"
                    pos="absolute"
                    right={4}
                    top={4}
                    style={{ zIndex: 10 }}
                    onClick={(e) => {
                        e.stopPropagation();
                        modals.openConfirmModal({
                            title: <Title order={3}>Clear Region</Title>,
                            children: <Text size="sm">Are you sure you want to remove this region?</Text>,
                            labels: {
                                confirm: "Delete",
                                cancel: "Cancel",
                            },
                            onConfirm() {
                                props.onDelete?.();
                            },
                        });
                    }}
                    aria-label="Clear region"
                >
                    <IconX size={16} />
                </ActionIcon>
            )}
            <Box pos="relative" style={{ width: "200px", height: "200px" }} tabIndex={-1}>
                <Suspense fallback={<Loader />}>
                    <MapBase widget id="message-audience-card">
                        <MapFeatureOverlay
                            features={features.concat(geofilters.map(({ data }) => ({ type: "Feature", properties: {}, geometry: data })))}
                        />
                    </MapBase>
                </Suspense>
            </Box>
        </Paper>
    );
};

const query = (geofilterId: string) =>
    queryOptions({
        queryKey: qk("map", "geofilters", geofilterId),
        queryFn: () =>
            httpPostGraphql(
                graphql(`
                    query MessageAudienceCardGeofilterById($id: UUID!) {
                        geoFilter(id: $id) {
                            id
                            boundary
                        }
                    }
                `),
                { id: geofilterId },
            ),
        select: ({ geoFilter: { boundary } }) => geozod.polygon().parse(JSON.parse(boundary)),
    });
